/**
 * Filterable single-select combobox. Pure UI — the parent owns the
 * candidate list and the selected value.
 *
 * Props
 * - value            currently-selected item (object) or null
 * - onChange(item)   called with the picked item, or null when cleared
 * - items            full candidate list (already loaded)
 * - getKey(item)     unique string identifying an item
 * - getSearch(item)  string used for filtering (matched substring, case-insensitive)
 * - renderItem(item, opts?) what to render in the row and the selected pill;
 *                    `opts.compact = true` is passed when rendering the
 *                    placeholder so it can return a short string.
 * - onQueryChange    optional — fires on every keystroke. Useful for
 *                    parents that want to use the typed text as a
 *                    free-text fallback when nothing is picked.
 * - placeholder, disabled, emptyText, testId — standard.
 *
 * Keyboard:
 *   ArrowUp/ArrowDown — move focus highlight
 *   Enter             — select the highlighted item
 *   Escape            — close dropdown
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, X } from "lucide-react";

export default function Combobox({
  value, onChange, items,
  getKey, getSearch, renderItem,
  onQueryChange,
  placeholder, disabled, emptyText = "No matches",
  testId,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 30);
    return items.filter((it) => getSearch(it).toLowerCase().includes(q)).slice(0, 50);
  }, [items, query, getSearch]);

  useEffect(() => { setFocusIdx(0); }, [filtered]);
  useEffect(() => { onQueryChange?.(query); }, [query, onQueryChange]);

  // Close on outside click.
  useEffect(() => {
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function pick(idx) {
    const it = filtered[idx];
    if (it == null) return;
    onChange?.(it);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(filtered.length - 1, i + 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(focusIdx); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className={`flex items-center gap-1.5 input ${disabled ? "opacity-60" : ""}`}>
        <Search size={13} className="text-neutral-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          placeholder={value ? renderItem(value, { compact: true }) : placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          className="flex-1 bg-transparent outline-none text-[13px] min-w-0"
          autoComplete="off"
          data-testid={testId}
        />
        {value && !query && (
          <button
            type="button"
            onClick={() => { onChange?.(null); setQuery(""); }}
            title="Clear"
            className="text-neutral-400 hover:text-black shrink-0"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-black/10 rounded-md shadow-lg max-h-72 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[12.5px] text-neutral-500">{emptyText}</div>
          ) : filtered.map((it, i) => {
            const isFocus = i === focusIdx;
            const isSelected = value && getKey(value) === getKey(it);
            return (
              <button
                type="button"
                key={getKey(it)}
                onMouseEnter={() => setFocusIdx(i)}
                onMouseDown={(e) => e.preventDefault()}  // keep focus on input
                onClick={() => pick(i)}
                className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center justify-between gap-2 ${
                  isFocus ? "bg-neutral-100" : "bg-white"
                }`}
              >
                <span className="min-w-0 truncate">{renderItem(it)}</span>
                {isSelected && <Check size={12} className="text-emerald-700 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
